import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const HistoryCard = ({ 
  snapshot, 
  isEditable, 
  onUpdateSnapshot, 
  onToggleExpanded, 
  isExpanded 
}) => {
  const [editingGoals, setEditingGoals] = useState({});
  const [editingCore4, setEditingCore4] = useState({});

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })?.format(new Date(date));
  };

  const getRelativeDate = (date) => {
    const today = new Date();
    const targetDate = new Date(date);
    const diffTime = today - targetDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return formatDate(date);
  };

  const handleGoalUpdate = (horizon, goalId, field, value) => {
    if (!isEditable) return;
    
    const updatedSnapshot = { ...snapshot };
    const goalIndex = updatedSnapshot?.goals?.[horizon]?.findIndex(g => g?.id === goalId);
    
    if (goalIndex !== -1) {
      updatedSnapshot.goals[horizon][goalIndex] = {
        ...updatedSnapshot?.goals?.[horizon]?.[goalIndex],
        [field]: value
      };
      onUpdateSnapshot(updatedSnapshot);
    }
  };

  const handleAddGoal = (horizon) => {
    if (!isEditable) return;
    
    const newGoal = {
      id: Date.now(),
      text: '',
      completed: false,
      createdAt: new Date()?.toISOString()
    };
    
    const updatedSnapshot = { ...snapshot };
    updatedSnapshot.goals[horizon] = [...updatedSnapshot?.goals?.[horizon], newGoal];
    onUpdateSnapshot(updatedSnapshot);
    
    setEditingGoals({ ...editingGoals, [newGoal?.id]: true });
  };

  const handleRemoveGoal = (horizon, goalId) => {
    if (!isEditable) return;
    
    const updatedSnapshot = { ...snapshot };
    updatedSnapshot.goals[horizon] = updatedSnapshot?.goals?.[horizon]?.filter(g => g?.id !== goalId);
    onUpdateSnapshot(updatedSnapshot);
  };

  const handleCore4Update = (area, field, value) => {
    if (!isEditable) return;
    
    const updatedSnapshot = { ...snapshot };
    updatedSnapshot.core4[area] = {
      ...updatedSnapshot?.core4?.[area],
      [field]: value
    };
    onUpdateSnapshot(updatedSnapshot);
  };

  const getCompletionSummary = () => {
    const totalGoals = Object.values(snapshot?.goals)?.reduce((sum, goals) => sum + goals?.length, 0);
    const completedGoals = Object.values(snapshot?.goals)?.reduce(
      (sum, goals) => sum + goals?.filter(g => g?.completed)?.length, 
      0
    );
    const core4Completed = Object.values(snapshot?.core4)?.filter(area => area?.completed)?.length;
    
    return { totalGoals, completedGoals, core4Completed };
  };

  const { totalGoals, completedGoals, core4Completed } = getCompletionSummary();

  return (
    <div className="bg-card border border-border rounded-lg shadow-gentle overflow-hidden">
      {/* Card Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors duration-200"
        onClick={() => onToggleExpanded(snapshot?.date)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                <Icon 
                  name="Calendar" 
                  size={20} 
                  className="text-primary"
                />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">
                  {getRelativeDate(snapshot?.date)}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {formatDate(snapshot?.date)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Summary Stats */}
            <div className="hidden sm:flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Icon name="Target" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {completedGoals}/{totalGoals} goals
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Heart" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {core4Completed}/4 CORE4
                </span>
              </div>
            </div>
            
            {/* Edit Badge */}
            {isEditable && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-accent/20 rounded-full">
                <Icon name="Edit3" size={12} className="text-accent" />
                <span className="text-xs font-medium text-accent">Editable</span>
              </div>
            )}
            
            {/* Expand Icon */}
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={20} 
              className="text-muted-foreground transition-transform duration-200"
            />
          </div>
        </div>
        
        {/* Mobile Summary */}
        <div className="sm:hidden mt-3 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span className="text-muted-foreground">
              {completedGoals}/{totalGoals} goals
            </span>
            <span className="text-muted-foreground">
              {core4Completed}/4 CORE4
            </span>
          </div>
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border">
          <div className="p-4 space-y-6">
            {/* Goals Section */}
            <div className="space-y-4">
              {['5Year', 'Daily', '90Day']?.map((horizon) => (
                <div key={horizon} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading font-medium text-foreground">
                      {horizon === '5Year' ? '5-Year Goals' : 
                       horizon === '90Day' ? '90-Day Goals' : 'Daily Goals'}
                    </h4>
                    {isEditable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Plus"
                        iconSize={16}
                        onClick={() => handleAddGoal(horizon)}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {snapshot?.goals?.[horizon]?.map((goal) => (
                      <div key={goal?.id} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Checkbox
                          checked={goal?.completed}
                          onChange={(e) => handleGoalUpdate(horizon, goal?.id, 'completed', e?.target?.checked)}
                          disabled={!isEditable}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          {editingGoals?.[goal?.id] && isEditable ? (
                            <Input
                              type="text"
                              value={goal?.text}
                              onChange={(e) => handleGoalUpdate(horizon, goal?.id, 'text', e?.target?.value)}
                              onBlur={() => setEditingGoals({ ...editingGoals, [goal?.id]: false })}
                              onKeyDown={(e) => {
                                if (e?.key === 'Enter') {
                                  setEditingGoals({ ...editingGoals, [goal?.id]: false });
                                }
                              }}
                              placeholder="Enter goal..."
                              className="text-sm"
                              autoFocus
                            />
                          ) : (
                            <p 
                              className={`text-sm cursor-pointer ${
                                goal?.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                              }`}
                              onClick={() => isEditable && setEditingGoals({ ...editingGoals, [goal?.id]: true })}
                            >
                              {goal?.text || 'Click to edit...'}
                            </p>
                          )}
                        </div>
                        {isEditable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="X"
                            iconSize={14}
                            onClick={() => handleRemoveGoal(horizon, goal?.id)}
                            className="text-error hover:text-error"
                          />
                        )}
                      </div>
                    ))}
                    
                    {snapshot?.goals?.[horizon]?.length === 0 && (
                      <p className="text-sm text-muted-foreground italic p-3 text-center">
                        No goals set for this day
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CORE4 Section */}
            <div className="space-y-3">
              <h4 className="font-heading font-medium text-foreground">CORE4 Daily Goals</h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(snapshot?.core4)?.map(([area, data]) => (
                  <div key={area} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={
                          area === 'faith' ? 'Heart' :
                          area === 'family' ? 'Users' :
                          area === 'finances' ? 'DollarSign' : 'Activity'
                        } 
                        size={16} 
                        className="text-primary"
                      />
                      <span className="font-medium text-foreground capitalize">{area}</span>
                    </div>
                    
                    {editingCore4?.[area] && isEditable ? (
                      <Input
                        type="text"
                        value={data?.goal}
                        onChange={(e) => handleCore4Update(area, 'goal', e?.target?.value)}
                        onBlur={() => setEditingCore4({ ...editingCore4, [area]: false })}
                        onKeyDown={(e) => {
                          if (e?.key === 'Enter') {
                            setEditingCore4({ ...editingCore4, [area]: false });
                          }
                        }}
                        placeholder={`Enter ${area} goal...`}
                        className="text-sm"
                        autoFocus
                      />
                    ) : (
                      <p 
                        className={`text-sm p-2 bg-muted/30 rounded cursor-pointer min-h-[2.5rem] flex items-center ${
                          data?.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                        }`}
                        onClick={() => isEditable && setEditingCore4({ ...editingCore4, [area]: true })}
                      >
                        {data?.goal || 'Click to edit...'}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={data?.completed}
                        onChange={(e) => handleCore4Update(area, 'completed', e?.target?.checked)}
                        disabled={!isEditable}
                        label="Completed"
                        size="sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryCard;